'use client';

import { useTheme } from '@emotion/react';
import { useEffect, useState } from 'react';
import { DateTime } from 'luxon';
import { useTimer } from 'react-timer-hook';
import RedMoneySVG from '../../public/img/redMoney.svg';
import CalendarSVG from '../../public/img/calendar.svg';
import DurationSVG from '../../public/img/duration.svg';
import LocationSVG from '../../public/img/location.svg';
import { Link } from '../styled/Link';
import { Action, City } from '../../models';
import { DEVICE_SCREENS_MAX_STRING } from '../../lib/consts';
import { isEventPassed } from '../../utils/event';
import { revealDate } from '../../utils/revealDate';
import { useModal } from '../../providers/modal';
import { YaMapModal } from '../ya-map/YaMapModal';
import { ACTION_TYPES } from '../../models/action-types';
import { countdownInit } from '../../utils/countdownInit';
import { getPriceWithCurrency } from '../../utils/getPriceWithCurrency';
import { getBenefit } from '../../utils/getBenefit';
import { getTitleForTimer } from '../../utils/getTitleForTimer';
import { formatTimerEvent } from '../../utils/formatTimerEvent';

type ITimerData = {
  showTimer: boolean;
  startDate: Date | null;
  endDate: Date | null;
  title: string | null;
};

export function AsideEventBlock({
  item,
  currentCity,
}: {
  item: Action;
  currentCity: City;
}) {
  const theme = useTheme();
  const [progressValue, setProgressValue] = useState(0);
  const modalState = useModal();

  const [timerData, setTimerData] = useState<ITimerData>({
    showTimer: false,
    startDate: null,
    endDate: null,
    title: null,
  });

  const { seconds, minutes, hours, days, restart } = useTimer({
    expiryTimestamp: timerData.endDate ?? new Date(),
    onExpire: () => console.warn('onExpire called'),
  });
  function getMicroDataDate(
    fieldName: 'startDate' | 'endDate',
    isDateUnknown: boolean
  ) {
    if (isDateUnknown) {
      return DateTime.now().toISO() as string;
    }
    if (fieldName === 'startDate' && item.nearest_event) {
      return DateTime.fromFormat(
        item.nearest_event.time.length > 0
          ? [item.nearest_event.start_date, item.nearest_event.time[0]].join(
              ' '
            )
          : item.nearest_event.start_date,
        item.nearest_event.time.length > 0
          ? 'yyyy-LL-dd HH:mm:ss'
          : 'yyyy-LL-dd'
      ).toISO() as string;
    }
    if (fieldName === 'endDate' && item.latest_event) {
      return DateTime.fromFormat(
        item.latest_event.time.length > 0
          ? [item.latest_event.start_date, item.latest_event.time[0]].join(' ')
          : item.latest_event.start_date,
        item.latest_event.time.length > 0 ? 'yyyy-LL-dd HH:mm:ss' : 'yyyy-LL-dd'
      ).toISO() as string;
    }
    return DateTime.now().toISO() as string;
  }
  useEffect(() => {
    const newTimerData: ITimerData = {
      showTimer: false,
      startDate: null,
      endDate: null,
      title: getTitleForTimer(item.countdown_type),
    };

    const dateList = revealDate(
      item.dates
        .filter(({ quotas }) => {
          if (quotas?.length === 0) return true;
          return quotas?.some(({ quantity }) => quantity > 0);
        })
        .map((date) => {
          if (date.time.length > 0) {
            return {
              interval: `${date.start_date} - ${date.end_date}`,
              time: date.time,
            };
          }
          // if there is no time before the end date next day at midnight
          return {
            interval: `${date.start_date} - ${DateTime.fromFormat(
              date.end_date,
              'yyyy-MM-dd'
            )
              .plus({ days: 1 })
              .toFormat('yyyy-MM-dd')}`,
            time: [],
          };
        })
    );

    const dateListWithTz = dateList.map((dateItem) => {
      const dateItemString = dateItem.toFormat('yyyy-MM-dd HH:mm:ss');
      return DateTime.fromFormat(dateItemString, 'yyyy-MM-dd HH:mm:ss')
        .setZone(currentCity.tz, { keepLocalTime: true })
        .setZone(DateTime.local().toFormat('z'));
    });

    const dataPrepareForTimer = countdownInit(
      item.countdown_type === 'manual'
        ? [DateTime.fromISO(item.countdown_datetime)]
        : dateListWithTz,
      item.countdown_type
    );

    if (dataPrepareForTimer) {
      newTimerData.showTimer = true;
      newTimerData.endDate = new Date(Date.now() + dataPrepareForTimer.date);
      newTimerData.startDate = DateTime.fromMillis(
        Date.now() + dataPrepareForTimer.date
      )
        .minus({
          weeks: 1,
        })
        .toJSDate();
    }

    if (newTimerData.showTimer) {
      const a = (Date.now() - Number(newTimerData.startDate?.getTime())) * 100;
      const b =
        Number(newTimerData.endDate?.getTime()) -
        Number(newTimerData.startDate?.getTime());
      const newProgressVal = Math.round(a / b);
      setProgressValue(newProgressVal);

      if (newTimerData.endDate) {
        restart(newTimerData.endDate);
      }
    }

    setTimerData(newTimerData);
  }, [item]);

  return (
    <div
      css={{
        backgroundColor: theme.colors.white,
        borderRadius: 4,
        padding: 30,
        marginBottom: 10,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        fontFamily: 'Golos',
        gap: 8,
        fontSize: 14,
        fontWeight: 500,
        lineHeight: '20px',
        letterSpacing: '0em',
        color: '#333',
        [DEVICE_SCREENS_MAX_STRING.md]: {
          padding: 20,
        },
      }}
    >
      {timerData.showTimer && (
        <div css={{ width: '100%', marginBottom: 12 }}>
          <div
            css={{
              fontSize: 14,
              fontFamily: 'Golos',
            }}
          >
            <span>{timerData.title}</span>
            <span>&nbsp;&nbsp;&nbsp;</span>
            <span
              css={{
                color: '#F44336',
              }}
            >
              {formatTimerEvent(days, hours, minutes, seconds)}
            </span>
          </div>

          <div
            css={{
              width: '100%',
              height: 4,
              background: '#7D7B7A',
              borderRadius: 2,
              marginTop: 5,
              overflow: 'hidden',
            }}
          >
            <div
              css={{
                height: '100%',
                width: `${progressValue}%`,
                background: '#F44336',
                borderRadius: 2,
                marginLeft: -1,
              }}
            />
          </div>
        </div>
      )}
      {item.type !== ACTION_TYPES.FREE && item.price && item.price !== '' && (
        <div
          title={
            item.instead_price && item.filter_price
              ? `Ваша экономия ${getPriceWithCurrency(
                  getBenefit(
                    item.instead_price,
                    item.type === 'tsnext_widget' ||
                      item.type === 'partner_widget'
                      ? Number(item.price_amount)
                      : Number(item.filter_price)
                  ),
                  item.price_currency
                )}`
              : ''
          }
          itemProp='offers'
          itemScope
          itemType='https://schema.org/Offer'
          css={{
            display: 'flex',
            gap: 8,
          }}
        >
          <meta itemProp='url' content='https://gorbilet.com' />
          {item.nearest_event && (
            <meta
              itemProp='validFrom'
              content={item.nearest_event.start_date}
            />
          )}
          {['active', 'deferred', 'hidden'].includes(item.status) ? (
            <meta
              itemProp='availability'
              content='https://schema.org/InStock'
            />
          ) : (
            <meta
              itemProp='availability'
              content='https://schema.org/SoldOut'
            />
          )}
          <div
            css={{ display: 'flex', alignItems: 'flex-start', marginTop: 3 }}
          >
            <RedMoneySVG css={{ width: 16, height: 16 }} />
          </div>
          <div>
            <div dangerouslySetInnerHTML={{ __html: item.price }} />
          </div>
          <meta
            itemProp='price'
            content={`${
              item.type === 'tsnext_widget' || item.type === 'partner_widget'
                ? item.price_amount ?? ''
                : item.filter_price ?? ''
            }`}
          />
          <meta itemProp='priceCurrency' content={item.price_currency} />
        </div>
      )}
      {item.type === ACTION_TYPES.FREE && (
        <div
          css={{
            display: 'flex',
            gap: 8,
          }}
        >
          <div css={{ display: 'flex', alignItems: 'center' }}>
            <RedMoneySVG css={{ width: 16, height: 16 }} />
          </div>
          <div>
            <div>Бесплатно</div>
          </div>
        </div>
      )}
      <div
        css={{
          display: 'flex',
          gap: 8,
          maxHeight: 280,
          overflow: 'auto',
          width: '100%',
          '&::-webkit-scrollbar': {
            width: 4,
            backgroundColor: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundImage: `-webkit-linear-gradient(90deg,
            transparent,
            rgba(0, 0, 0, 0) 0%,
            rgba(244, 67, 54, 0.3) 10%,
            rgba(244, 67, 54, 0.9) 20%,
            rgba(244, 67, 54, 1) 50%,
            rgba(244, 67, 54, 0.9) 80%,
            rgba(244, 67, 54, 0.3) 90%,
            rgba(0, 0, 0, 0) 100%,
            transparent)`,
          },
        }}
      >
        {!isEventPassed(item) && item.dates_txt_detailed.length > 0 ? (
          <>
            <div css={{ display: 'flex', alignItems: 'flex-start' }}>
              <CalendarSVG css={{ width: 16, fill: theme.colors.red }} />
            </div>
            <div
              css={{ display: 'flex', flexDirection: 'column' }}
              dangerouslySetInnerHTML={{
                __html: item.dates_txt_detailed
                  .map((date) =>
                    Object.entries(date).map(([month, datesDays]) => {
                      const daysSeparator = month ? '<br/>' : ' и ';
                      return [month, datesDays.join(daysSeparator)]
                        .filter(Boolean)
                        .join(`<br/>`);
                    })
                  )
                  .join(`<br/>`),
              }}
            />
            <meta
              itemProp='startDate'
              content={getMicroDataDate('startDate', false)}
            />
            {item.with_open_date && (
              <meta
                itemProp='endDate'
                content={getMicroDataDate('endDate', false)}
              />
            )}
          </>
        ) : (
          <span
            css={{
              display: 'flex',
              gap: 8,
            }}
          >
            {item.date_unknown && (
              <>
                <div css={{ display: 'flex', alignItems: 'flex-start' }}>
                  <CalendarSVG css={{ width: 16, fill: theme.colors.red }} />
                </div>
                <p>Дата уточняется</p>
                <meta
                  itemProp='startDate'
                  content={getMicroDataDate('startDate', true)}
                />
                {item.with_open_date && (
                  <meta
                    itemProp='endDate'
                    content={getMicroDataDate('endDate', true)}
                  />
                )}
              </>
            )}
          </span>
        )}
      </div>
      {item.duration && item.duration !== '' && (
        <div
          css={{
            display: 'flex',
            gap: 8,
          }}
        >
          <div css={{ display: 'flex', alignItems: 'flex-start' }}>
            <DurationSVG css={{ width: 16, fill: theme.colors.red }} />
          </div>
          <div itemProp='duration'>{item.duration}</div>
        </div>
      )}
      {item.places.length && (
        <div
          css={{
            display: 'flex',
            gap: 8,
          }}
        >
          <div css={{ display: 'flex', alignItems: 'flex-start' }}>
            <LocationSVG css={{ width: 16, fill: theme.colors.red }} />
          </div>
          <div css={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {item.places &&
              item.places.length > 0 &&
              item.places.map((place) => (
                <div
                  itemProp='location'
                  itemScope
                  itemType='https://schema.org/Place'
                  key={place.title}
                  css={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    gap: 4,
                  }}
                >
                  <Link
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    css={{
                      fontFamily: 'Golos',
                      fontSize: '14',
                      fontWeight: 500,
                      lineHeight: '20px',
                      marginBottom: 10,
                      display: 'inline-block',
                      textDecoration: 'underline',
                      textDecorationStyle: 'dashed',
                      color: theme.colors.red,
                      ':hover': {
                        color: theme.colors.red,
                        textDecorationStyle: 'solid',
                      },
                    }}
                    href={['', 'places', place.slug, ''].join('/')}
                  >
                    <meta itemProp='name' content={place.title} />
                    {place.title}
                  </Link>
                  <div
                    itemProp='address'
                    itemScope
                    itemType='https://schema.org/PostalAddress'
                  >
                    <meta itemProp='addressLocality' content={place.title} />
                  </div>

                  {place.addresses.map((address, index) => (
                    <>
                      <div css={{ lineHeight: '20px' }} key={address}>
                        {address}
                      </div>
                      {place.coordinates_list[index] && (
                        <button
                          key={place.coordinates_list[index]}
                          css={{
                            fontFamily: 'Golos',
                            fontSize: 14,
                            fontWeight: 500,
                            lineHeight: '20px',
                            textDecoration: 'none',
                            cursor: 'pointer',
                            borderBottom: '1px dashed rgba(0, 0, 0, 0.5)',
                            color: theme.colors.black,
                            ':hover': {
                              borderBottom: '1px solid rgba(0, 0, 0, 0.5)',
                            },
                          }}
                          type='button'
                          onClick={() => {
                            if (modalState) {
                              modalState.setModalState((oldState) => ({
                                ...oldState,
                                show: true,
                                content: (
                                  <YaMapModal
                                    cords={place.coordinates_list[index]}
                                    setModalState={modalState.setModalState}
                                  />
                                ),
                                options: { width: 800 },
                              }));
                            }
                          }}
                        >
                          Показать на карте
                        </button>
                      )}
                    </>
                  ))}
                </div>
              ))}
          </div>
        </div>
      )}
    </div>
  );
}
